import { useCallback, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { IssueCommentDto } from '@aiai/shared';
import { CheckCircle, Play, Trash2 } from 'lucide-react';
import { addIssueComment, deleteIssueComment } from '../api';
import { qk } from '../api/queryKeys';
import { formatRelative } from '../lib/format';
import { errorMessage } from '../lib/api';
import { useImageUpload } from '../hooks/useImageUpload';
import { Callout } from './Callout';
import { ConfirmDialog } from './ConfirmDialog';
import { Markdown } from './Markdown';
import { Spinner } from './Spinner';

/**
 * One user-authored comment: author, timestamp, markdown body, and
 * — for the author or an admin — a confirm-guarded delete. Rendered inline in
 * the issue timeline so comments interleave chronologically with agent activity.
 */
export function CommentCard({
  comment,
  issueId,
  currentUserId,
  canModerate,
  readOnly = false,
}: {
  comment: IssueCommentDto;
  issueId: string;
  currentUserId: string;
  /** Workspace owner: may delete anyone's comment. A non-owner may delete only
   *  their own. (No platform-wide wildcard.) */
  canModerate: boolean;
  readOnly?: boolean;
}) {
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const canDelete = !readOnly && (canModerate || comment.authorId === currentUserId);

  const deleteMutation = useMutation({
    mutationFn: () => deleteIssueComment(issueId, comment.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.issueComments(issueId) });
      setConfirmOpen(false);
    },
  });

  return (
    <>
      <div className="relative rounded-md border border-line bg-surface-2 px-3 py-2.5">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-ink">{comment.authorEmail}</span>
            <span className="text-2xs text-ink-faint">{formatRelative(comment.createdAt)}</span>
          </div>
          {canDelete && (
            <button
              type="button"
              className="btn-ghost p-1 text-ink-faint hover:text-danger"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirmOpen(true)}
              aria-label="Delete comment"
            >
              {deleteMutation.isPending ? (
                <Spinner className="h-3 w-3" />
              ) : (
                <Trash2 className="h-3 w-3" />
              )}
            </button>
          )}
        </div>
        <Markdown className="text-sm">{comment.body}</Markdown>
        {deleteMutation.isError && (
          <p className="mt-1.5 text-xs text-danger">
            {errorMessage(deleteMutation.error, 'Could not delete comment')}
          </p>
        )}
      </div>
      <ConfirmDialog
        open={confirmOpen}
        title="Delete comment?"
        message="This comment will be permanently deleted."
        confirmLabel="Delete"
        busy={deleteMutation.isPending}
        error={deleteMutation.isError ? errorMessage(deleteMutation.error, 'Could not delete comment') : null}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  );
}

/**
 * Compose box that appends a comment to the issue timeline. Supports pasting or
 * dropping images (uploaded and embedded as markdown) and ⌘/Ctrl+Enter to submit.
 * Lives at the foot of the timeline feed.
 */
export function CommentComposer({
  issueId,
  heldForYou = false,
  onApprove,
  approving = false,
  runnable = false,
  runAgain = false,
  onRun,
  running = false,
}: {
  issueId: string;
  /** Proposed plan awaiting YOUR approval → the composer is the action bar:
   *  Send back (a comment that wakes the Planner) + Approve & build. */
  heldForYou?: boolean;
  onApprove?: () => void;
  approving?: boolean;
  /** Open/failed issue you can build → the composer shows Run / Run again, so
   *  every "advance this issue" action lives in one place at the bottom. */
  runnable?: boolean;
  runAgain?: boolean;
  onRun?: () => void;
  running?: boolean;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const addMutation = useMutation({
    mutationFn: () => addIssueComment(issueId, draft.trim()),
    onSuccess: () => {
      setDraft('');
      queryClient.invalidateQueries({ queryKey: qk.issueComments(issueId) });
      queryClient.invalidateQueries({ queryKey: qk.issueTimeline(issueId) });
    },
  });

  const insertAtCursor = useCallback((snippet: string) => {
    const el = textareaRef.current;
    if (!el) {
      setDraft((prev) => prev + '\n' + snippet + '\n');
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const next = el.value.slice(0, start) + '\n' + snippet + '\n' + el.value.slice(end);
    setDraft(next);
    requestAnimationFrame(() => {
      const pos = start + snippet.length + 2;
      el.setSelectionRange(pos, pos);
      el.focus();
    });
  }, []);

  const { uploading: imageUploading, uploadError: imageUploadError, onPaste, onDrop, onDragOver } =
    useImageUpload({ onInsert: insertAtCursor });

  return (
    <div
      className={
        heldForYou
          ? 'space-y-2 rounded-lg border border-signal/40 bg-signal/5 p-3'
          : 'space-y-2'
      }
    >
      <div>
        <p className={heldForYou ? 'text-xs font-semibold text-signal' : 'text-xs font-semibold text-ink'}>
          {heldForYou ? 'Plan awaiting your approval' : 'Comment'}
        </p>
        <p className="text-2xs text-ink-muted">
          {heldForYou
            ? 'Approve to build, or write what to change and send it back.'
            : 'Added to the timeline; agents read new comments on their next turn.'}
        </p>
      </div>
      <textarea
        ref={textareaRef}
        className="w-full rounded-md border border-line bg-surface-2 px-3 py-2 text-sm text-ink placeholder:text-ink-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
        rows={3}
        maxLength={10000}
        placeholder={
          heldForYou ? 'Optional note — required to send back…' : 'Write a comment…'
        }
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={addMutation.isPending || imageUploading}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && draft.trim()) {
            addMutation.mutate();
          }
        }}
        onPaste={onPaste}
        onDrop={onDrop}
        onDragOver={onDragOver}
      />
      <div className="flex items-center justify-between gap-2">
        <span className="text-2xs text-ink-faint">
          {imageUploading ? 'Uploading image…' : ''}
        </span>
        <div className="flex items-center gap-2">
          {heldForYou && onApprove ? (
            <>
              <button
                type="button"
                className="btn-secondary"
                disabled={!draft.trim() || addMutation.isPending || imageUploading}
                onClick={() => addMutation.mutate()}
                title={!draft.trim() ? 'Write a note to send it back' : 'Send back to the Planner'}
              >
                {addMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
                Send back
              </button>
              <button type="button" className="btn-primary" disabled={approving} onClick={onApprove}>
                {approving ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <CheckCircle className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                Approve &amp; build
              </button>
            </>
          ) : runnable && onRun ? (
            <>
              {draft.trim() && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={addMutation.isPending || imageUploading}
                  onClick={() => addMutation.mutate()}
                >
                  {addMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
                  Comment
                </button>
              )}
              <button type="button" className="btn-primary" disabled={running} onClick={onRun}>
                {running ? (
                  <Spinner className="h-3.5 w-3.5" />
                ) : (
                  <Play className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                {runAgain ? 'Run again' : 'Run'}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={!draft.trim() || addMutation.isPending || imageUploading}
              onClick={() => addMutation.mutate()}
            >
              {addMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
              Comment
            </button>
          )}
        </div>
      </div>
      {imageUploadError && (
        <Callout tone="danger">{imageUploadError}</Callout>
      )}
      {addMutation.isError && (
        <Callout tone="danger">
          {errorMessage(addMutation.error, 'Could not add comment')}
        </Callout>
      )}
    </div>
  );
}
